import axios, {
  AxiosInstance,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import createAuthRefreshInterceptor, {
  AxiosAuthRefreshRequestConfig,
} from "axios-auth-refresh";
import { AUTH_STATUS_CODES } from "../../../../../utils/common/constants/apis";
import {
  EXLY_API_ERROR_STATUS_CODES,
  JWT_GRANT_TYPES,
  TOKEN_TYPE,
} from "../constants/DataProvider.constants";
import { request_tokens_api } from "../../../../EUD/modules/Onboarding/constants/Onboarding.apis";
import { IOnboarding } from "../../../../EUD/modules/Onboarding/IOnboarding";

// Axios instance with credentials (to enable HttpOnly cookies) included
let internalApiClient: AxiosInstance;

// Global variables for configuring the Axios client
let baseURL: string | undefined;

/**
 * Request interceptor to attach an access token to outgoing requests.
 * If a request is marked to skip token refresh, it bypasses token injection.
 * Additionally, waits for token initialization if it's in progress.
 * @param {InternalAxiosRequestConfig} request - Axios request configuration.
 */
const requestInterceptor = async ({
  request,
  accessToken,
}: {
  request: InternalAxiosRequestConfig;
  accessToken: string;
}) => {
  const skipAccessToken = (request as AxiosAuthRefreshRequestConfig)
    .skipAuthRefresh;

  // Check if the request should skip auth token injection
  if (skipAccessToken) return request;

  if (accessToken) request.headers["jwt-token"] = accessToken;

  return request;
};

/**
 * Configures the Axios client instance.
 * @param {object} params - Configuration parameters.
 * @param {string} params.baseURL - Base URL for the Axios instance.
 * @param {object} params.headers - Additional headers to include in requests.
 * @param {function} params.setAccessToken - Function to set the access token.
 * @param {function} params.getAccessToken - Function to get the access token.
 */
export const configureInternalApiClient = ({
  baseURL: inputBaseURL,
  headers = {},
  setTokens,
  getTokens,
  tokenSelector,
  subDomain,
  sessionId,
  customerUsername,
  handleAuthRefreshFailure,
  userAgent,
}: {
  baseURL?: string;
  userAgent?: string;
  customerUsername?: string;
  subDomain?: string;
  sessionId?: string;
  headers?: { [headerName: string]: string };
  setTokens: (token: IOnboarding.IRequestTokensResponse["tokens"]) => void;
  getTokens: () => IOnboarding.IRequestTokensResponse["tokens"];
  tokenSelector: (
    response: AxiosResponse,
  ) => IOnboarding.IRequestTokensResponse["tokens"];
  handleAuthRefreshFailure?: (err: { message: string; status: number }) => void;
}) => {
  internalApiClient = axios.create({
    withCredentials: true,
    baseURL: inputBaseURL,
    headers,
  });
  // Set the base URL for Axios
  if (inputBaseURL) {
    baseURL = inputBaseURL;
  }

  const requestErrorInterceptor = (error) => Promise.reject(error);

  internalApiClient.interceptors.request.use(
    (request) =>
      requestInterceptor({ request, accessToken: getTokens()?.access_token }),
    requestErrorInterceptor,
  );

  internalApiClient.interceptors.response.use(
    (response) => {
      // adding this check becuase legacy API_CLIENT code uses response without parsing
      if (
        response.config?.fetchOptions &&
        response.config?.fetchOptions["isLegacy"]
      ) {
        return response;
      }

      return response?.data;
    },
    (error) => {
      // If there's a response, return the response data directly
      // adding this check becuase legacy API_CLIENT code throws only api error
      if (
        error.config?.fetchOptions &&
        error.config?.fetchOptions["isLegacy"] &&
        error.response &&
        error.response.data
      ) {
        return Promise.reject(error.response.data);
      }
      const { status } = error?.response?.data ?? {};
      if (status === EXLY_API_ERROR_STATUS_CODES.UNKNOWN_USER.status) {
        const errData = error?.response?.data ?? error;
        if (handleAuthRefreshFailure) {
          return handleAuthRefreshFailure(errData);
        }
      }
      // fallback for network errors or unexpected errors
      return Promise.reject(error);
    },
  );

  /**
   * Handles token refresh logic for failed requests.
   * @param {object} failedRequest - The failed Axios request.
   */
  const handleRefreshAuth = (failedRequest) => {
    const payload = {
      grant_type: JWT_GRANT_TYPES.REFRESH_TOKEN,
      customer_username: customerUsername,
      token_type: TOKEN_TYPE.CUSTOMER_LOGIN,
      refresh_token: getTokens()?.refresh_token,
      sub_domain: subDomain,
      session_id: sessionId,
      user_agent: userAgent,
    };

    return axios
      .post(request_tokens_api, payload, {
        withCredentials: true,
        baseURL,
        headers,
      })
      .then((tokenRefreshResponse) => {
        const newAccessToken = tokenSelector(tokenRefreshResponse?.data);
        setTokens(newAccessToken);
        failedRequest.response.config.headers["jwt-token"] =
          newAccessToken.access_token;
        return Promise.resolve();
      })
      .catch((error) => {
        const errData = error?.response?.data ?? error;
        if (handleAuthRefreshFailure) handleAuthRefreshFailure(errData);
        console.log({ handleRefreshAutherror: errData });
      });
  };

  // Attach refresh logic using the refresh interceptor
  createAuthRefreshInterceptor(getInternalApiClient(), handleRefreshAuth, {
    shouldRefresh: (error: any) => {
      const errorStatus = error?.response?.data?.status ?? error?.status;
      return errorStatus === AUTH_STATUS_CODES.ACCESS_TOKEN_EXPIRED;
    },
  });
};

// Export the Axios instance
export const getInternalApiClient = (): AxiosInstance => {
  if (!internalApiClient) {
    // Ensure apiClient is initialized
    throw new Error(
      "API client is not configured. Please call `configureApiClient` first.",
    );
  }
  return internalApiClient;
};
